import React, { useEffect, useState } from "react";
import Modal from "../../../components/modal";
import axios from "axios";
import Swal from "sweetalert2";
import { API_URL } from "../../../utils/constant";
import { Country, State, City } from "country-state-city";



const CompanyLocationModal = ({
  show,
  onClose,
  heading,
  initialData,
  onSuccess,
}) => {
  const initialFormState = {
    name: "",
    company_id: "",
    code: "",
    gstNo: "",
    address: "",
    country: "India",
    state: "",
    city: "",
    pincode: "",
    image: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);



  // 🔹 Generate Code automatically
  const generateCode = (name) => {
    return name
      ?.toUpperCase()
      .replace(/\s+/g, "_")
      .substring(0, 10);
  };

  useEffect(() => {
    const company_id = localStorage.getItem("companyID");

    if (initialData) {
      setFormData({ ...initialData });
      setStates(State.getStatesOfCountry(initialData.country));
      setCities(
        City.getCitiesOfState(initialData.country, initialData.state)
      );
    } else {
      setFormData({
        ...initialFormState,
        company_id,
        country: 'India',
      });
      setStates(State.getStatesOfCountry('India'));
      setCities([]);
    }
  }, [initialData, show]);

  // 🔹 Handle form change
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Auto-generate code from name
    if (name === "name") {
      setFormData((prev) => ({
        ...prev,
        name: value,
        code: generateCode(value),
      }));
      return;
    }

    // Country change
    if (name === "country") {
      setStates(State.getStatesOfCountry(value));
      setCities([]);
      setFormData((prev) => ({
        ...prev,
        country: value,
        state: "",
        city: "",
      }));
      return;
    }

    // State change
    if (name === "state") {
      setCities(City.getCitiesOfState(formData.country, value));
      setFormData((prev) => ({
        ...prev,
        state: value,
        city: "",
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.country || !formData.state) {
      Swal.fire("Validation Error", "Required fields missing", "warning");
      return;
    }

    try {
      setLoading(true);

      if (initialData?.id) {
        await axios.put(
          `${API_URL}/company-locations/${initialData.id}`,
          formData
        );
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "The Location has been updated.",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          didOpen: (toast) => {
            toast.addEventListener("mouseenter", Swal.stopTimer);
            toast.addEventListener("mouseleave", Swal.resumeTimer);
          },
        });
      } else {
        await axios.post(`${API_URL}/company-locations`, formData);
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "The Location has been created.",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          didOpen: (toast) => {
            toast.addEventListener("mouseenter", Swal.stopTimer);
            toast.addEventListener("mouseleave", Swal.resumeTimer);
          },
        });
      }

      onClose();
      onSuccess();
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Something went wrong",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onClose={onClose} heading={heading}>
      <form onSubmit={handleSubmit}>
        <div className="row">
          {/* Location Name */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Location Name</label>
            <input
              type="text"
              className="form-control"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          {/* Auto Code */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Code</label>
            <input
              type="text"
              className="form-control"
              value={formData.code}
              readOnly
            />
          </div>

          {/* GST */}
          <div className="col-md-6 mb-3">
            <label className="form-label">GST No</label>
            <input
              type="text"
              className="form-control"
              name="gstNo"
              value={formData.gstNo}
              onChange={handleChange}
            />
          </div>

          {/* Country */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Country</label>
            <select
              className="form-select"
              name="country"
              value={formData.country}
              onChange={handleChange}
              required
            >
              <option value="">Select Country</option>
              {Country.getAllCountries().map((c) => (
                <option key={c.isoCode} value={c.isoCode}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* State */}
          <div className="col-md-6 mb-3">
            <label className="form-label">State</label>
            <select
              className="form-select"
              name="state"
              value={formData.state}
              onChange={handleChange}
              disabled={!states.length}
              required
            >
              <option value="">Select State</option>
              {states.map((s) => (
                <option key={s.isoCode} value={s.isoCode}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          {/* City */}
          <div className="col-md-6 mb-3">
            <label className="form-label">City</label>
            <select
              className="form-select"
              name="city"
              value={formData.city}
              onChange={handleChange}
              disabled={!cities.length}
            >
              <option value="">Select City</option>
              {cities.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Pincode */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Pincode</label>
            <input
              type="text"
              className="form-control"
              name="pincode"
              value={formData.pincode}
              onChange={handleChange}
            />
          </div>

          {/* Address */}
          <div className="col-12 mb-3">
            <label className="form-label">Address</label>
            <textarea
              className="form-control"
              name="address"
              value={formData.address}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="d-flex justify-content-end">
          <button
            type="button"
            className="btn btn-light me-2"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CompanyLocationModal;
